// MARK://dhk8/pos/hooks/use-cart
// Tiny client-side cart for the fast-bar / to-go flow (no server order until checkout).
// The seated-table flow uses order-store instead (the order is created up-front there).
//
// Lines are keyed by `lineId`: a plain product gets `p:<productId>` so re-tapping it
// just bumps the quantity; a *customized* product (variant / modifiers — added via the
// AddItemDialog sheet) gets `d:<productId>:<signature>` so identical customizations
// merge while different ones stay separate. `unitPriceCents` already includes any
// variant / modifier deltas; `modifiers` carries the chosen option names for display.

import { useCallback, useEffect, useMemo, useState } from 'react'

export interface CartLineModifier { name: string; priceDeltaCents: number }

export interface CartLine {
  /** Stable per-line key (see header). Use this for +/− / remove, not productId. */
  lineId: string
  productId: string
  name: string
  unitPriceCents: number
  quantity: number
  taxRate: string
  /** Chosen variant + modifier option names (display only) — empty for plain lines. */
  modifiers: CartLineModifier[]
  notes?: string
}

export interface CartItemInput {
  productId: string
  name: string
  unitPriceCents: number
  taxRate?: string
}

/** A customized line built by AddItemDialog (its DraftItem). */
export interface CartDraftInput {
  productId: string
  name: string
  /** Unit price WITH variant + modifier deltas already applied. */
  unitPriceCents: number
  taxRate?: string | number
  quantity: number
  modifiers?: { name: string; priceDeltaCents: number }[]
  variantId?: string
  variantName?: string
  notes?: string
}

function draftSignature(d: CartDraftInput): string {
  const mods = (d.modifiers ?? []).map((m) => m.name).slice().sort().join('|')
  return `${d.variantId ?? ''}#${mods}#${d.notes ?? ''}`
}

// ── Persistencia del carrito (fix TCK-0013) ────────────────────────────────
// El carrito de barra vivía solo en memoria → se perdía al auto-lock (5 min) /
// relanzamiento de la PWA por el SO (tablet en background). Lo persistimos en
// localStorage (sobrevive recarga/relaunch, no solo re-render) con una guarda de
// frescura para no resucitar un carrito abandonado de hace horas.
const CART_KEY = 'dhk8_pos_barcart'
const CART_TTL_MS = 12 * 60 * 60 * 1000 // 12h

function loadCart(): CartLine[] {
  try {
    const raw = window.localStorage.getItem(CART_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as { ts?: number; lines?: CartLine[] }
    if (!parsed || !Array.isArray(parsed.lines)) return []
    if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > CART_TTL_MS) {
      window.localStorage.removeItem(CART_KEY)
      return []
    }
    return parsed.lines
  } catch {
    return []
  }
}

function saveCart(lines: CartLine[]): void {
  try {
    if (lines.length === 0) window.localStorage.removeItem(CART_KEY)
    else window.localStorage.setItem(CART_KEY, JSON.stringify({ ts: Date.now(), lines }))
  } catch {
    /* noop — quota/privacy mode; degradación a solo-memoria */
  }
}

export function useCart() {
  const [lines, setLines] = useState<CartLine[]>(loadCart)
  // Persistir en cada cambio; vaciar borra la clave (ver saveCart).
  useEffect(() => { saveCart(lines) }, [lines])

  const add = useCallback((p: CartItemInput) => {
    const lineId = `p:${p.productId}`
    setLines((prev) => {
      const found = prev.find((l) => l.lineId === lineId)
      if (found) return prev.map((l) => (l.lineId === lineId ? { ...l, quantity: l.quantity + 1 } : l))
      return [...prev, { lineId, productId: p.productId, name: p.name, unitPriceCents: p.unitPriceCents, quantity: 1, taxRate: p.taxRate ?? '0', modifiers: [] }]
    })
  }, [])

  const addDraft = useCallback((d: CartDraftInput) => {
    const lineId = `d:${d.productId}:${draftSignature(d)}`
    const mods: CartLineModifier[] = [
      ...(d.variantName ? [{ name: d.variantName, priceDeltaCents: 0 }] : []),
      ...(d.modifiers ?? []),
    ]
    setLines((prev) => {
      const found = prev.find((l) => l.lineId === lineId)
      if (found) return prev.map((l) => (l.lineId === lineId ? { ...l, quantity: l.quantity + d.quantity } : l))
      return [...prev, {
        lineId, productId: d.productId, name: d.name, unitPriceCents: d.unitPriceCents,
        quantity: d.quantity, taxRate: String(d.taxRate ?? '0'), modifiers: mods,
        ...(d.notes ? { notes: d.notes } : {}),
      }]
    })
  }, [])

  /** Bump a known line by +1 (used by the in-cart "+" button). */
  const increment = useCallback((lineId: string) => {
    setLines((prev) => prev.map((l) => (l.lineId === lineId ? { ...l, quantity: l.quantity + 1 } : l)))
  }, [])

  const decrement = useCallback((lineId: string) => {
    setLines((prev) => prev.map((l) => (l.lineId === lineId ? { ...l, quantity: l.quantity - 1 } : l)).filter((l) => l.quantity > 0))
  }, [])

  const remove = useCallback((lineId: string) => {
    setLines((prev) => prev.filter((l) => l.lineId !== lineId))
  }, [])

  const clear = useCallback(() => setLines([]), [])

  const subtotalCents = useMemo(() => lines.reduce((s, l) => s + l.unitPriceCents * l.quantity, 0), [lines])
  const itemCount = useMemo(() => lines.reduce((s, l) => s + l.quantity, 0), [lines])

  return { lines, add, addDraft, increment, decrement, remove, clear, subtotalCents, itemCount }
}
